import React, { useEffect, useState } from "react";
import { KeyRound, Save, ShieldOff, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/auth";
import { Btn, Choice, Field, Modal, PermGrid, SecretBox, inputCls } from "./kit";

const FIELDS = ["name", "email", "title", "role", "status", "mobile_phone_number", "sms_alerts_enabled"];

export const StaffEditDialog = ({ user, meta, me, onClose, onSaved }) => {
  const isNew = user && !user.id;
  const [f, setF] = useState({});
  const [secret, setSecret] = useState("");
  const [sent, setSent] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!user) return;
    setSecret(""); setSent("");
    setF({ name: "", email: "", title: "", role: "staff", status: "active", mobile_phone_number: "", sms_alerts_enabled: false, ...user,
      perms: Object.fromEntries((user.staff_permissions || []).map((k) => [k, true])) });
  }, [user]);
  if (!user || !meta) return null;
  const self = user.id === me?.id;
  const locked = self || user.is_owner;
  const sa = f.role === "super_admin";
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

  const save = async () => {
    setBusy(true);
    const staff_permissions = Object.keys(f.perms).filter((k) => f.perms[k]);
    try {
      if (isNew) {
        const { data } = await apiClient.post("/console/users", { name: f.name, email: f.email, role: f.role, title: f.title, mobile_phone_number: f.mobile_phone_number, staff_permissions });
        setSecret(data.temporary_password); setSent(data.email_sent ? f.email : "");
        toast.success(data.email_sent ? `User created · PIN setup steps emailed to ${f.email}` : "User created · email not sent. They can tap 'Get a code by email' on the sign-in page to set their PIN.");
      } else {
        const body = Object.fromEntries(FIELDS.filter((k) => (f[k] ?? "") !== (user[k] ?? "")).map((k) => [k, f[k]]));
        if (staff_permissions.sort().join() !== [...(user.staff_permissions || [])].sort().join()) body.staff_permissions = staff_permissions;
        if (!Object.keys(body).length) { toast.message("Nothing changed"); setBusy(false); return; }
        await apiClient.patch(`/console/users/${user.id}`, body); toast.success("Saved"); onClose();
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  const act = async (fn, okMsg) => { try { await fn(); toast.success(okMsg); onSaved(); } catch (e) { toast.error(formatApiError(e)); } };
  const resetPw = () => act(async () => {
    const { data } = await apiClient.post(`/console/users/${user.id}/reset-password`, { send_email: true });
    setSecret(data.temporary_password); setSent(data.email_sent ? user.email : "");
  }, "PIN cleared · setup email sent");
  const clearPin = () => window.confirm("Clear this user's PIN?") && act(() => apiClient.delete(`/console/users/${user.id}/pin`), "PIN cleared");
  const remove = async () => {
    if (!window.confirm(`Remove ${user.name}? They lose all access immediately, and any residents they case-manage will have no case manager until you pick someone.`)) return;
    try {
      const { data } = await apiClient.delete(`/console/users/${user.id}`);
      toast.success(`User removed${data.case_manager_cleared ? ` · case manager cleared on ${data.case_manager_cleared} resident(s)` : ""}`);
      onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Modal open onClose={onClose} title={isNew ? "Add team member" : `Edit · ${user.name}`} testId="staff-edit-dialog">
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Name"><input data-testid="staff-name" className={inputCls} value={f.name || ""} onChange={set("name")} /></Field>
          <Field label="Login email" hint={user.is_owner ? "Primary owner email is protected" : null}>
            <input data-testid="staff-email" className={inputCls} value={f.email || ""} onChange={set("email")} disabled={user.is_owner} /></Field>
          <Field label="Title"><input data-testid="staff-title" className={inputCls} value={f.title || ""} onChange={set("title")} /></Field>
          <Field label="Mobile (E.164)"><input data-testid="staff-phone" className={inputCls} value={f.mobile_phone_number || ""} onChange={set("mobile_phone_number")} placeholder="+15125551234" /></Field>
          <Field label="Role" hint={locked && !isNew ? "You can't change your own or the owner's role" : null}>
            <Choice testId="staff-role" value={f.role} onChange={set("role")} options={meta.roles} disabled={locked && !isNew} /></Field>
          {!isNew && <Field label="Account status"><Choice testId="staff-status" value={f.status} onChange={set("status")} options={meta.user_statuses} disabled={locked} /></Field>}
        </div>
        <label className="flex items-center gap-2 text-sm text-white/80">
          <input data-testid="staff-sms" type="checkbox" checked={!!f.sms_alerts_enabled} onChange={set("sms_alerts_enabled")} /> SMS alerts enabled
        </label>
        <div className="space-y-2">
          <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-white/50">Permissions {sa && "· Super Admins always have every permission"}</div>
          <PermGrid catalog={meta.staff_permissions} value={sa ? Object.fromEntries(Object.keys(meta.staff_permissions).map((k) => [k, true])) : f.perms || {}}
            disabled={sa} testPrefix="staff-perm" onToggle={(k) => setF((p) => ({ ...p, perms: { ...p.perms, [k]: !p.perms[k] } }))} />
        </div>
        {secret && <SecretBox label="Temporary password" value={secret} testId="staff-temp-password" />}
        {secret && (
          <p data-testid="staff-access-email-status" className="text-xs text-white/60">
            {sent ? `Sign-in details were emailed to ${sent}.` : "The email could not be sent. Copy the password above and share it securely."} They must choose their own password at first sign-in.
          </p>
        )}
        <div className="flex flex-wrap gap-2 justify-between pt-2">
          <div className="flex gap-2">
            {!isNew && <Btn tone="muted" data-testid="staff-reset-password" onClick={resetPw}><KeyRound size={12} /> Reset PIN &amp; email setup</Btn>}
            {!isNew && user.has_pin && <Btn tone="red" data-testid="staff-clear-pin" onClick={clearPin}><ShieldOff size={12} /> Clear PIN</Btn>}
            {!isNew && !locked && <Btn tone="red" data-testid="staff-remove" onClick={remove}><Trash2 size={12} /> Remove</Btn>}
          </div>
          {!(isNew && secret) && <Btn data-testid="staff-save" onClick={save} disabled={busy}><Save size={12} /> {isNew ? "Create" : "Save changes"}</Btn>}
        </div>
      </div>
    </Modal>
  );
};
